import React from 'react';
import { X } from 'lucide-react';

const themes = [
  {
    id: 'original',
    name: 'Original',
    description: 'Classic blue & emerald',
    swatch: ['#2563eb', '#16a34a', '#f8fafc'],
  },
  {
    id: 'woods-earth',
    name: 'Woods & Earth',
    description: 'Forest greens & bark browns',
    swatch: ['#3d5a3e', '#7c5c3a', '#f5f0e8'],
  },
  {
    id: 'coral-reef',
    name: 'Coral Reef',
    description: 'Warm coral & turquoise',
    swatch: ['#e8613a', '#0e9090', '#fffaf7'],
  },
  {
    id: 'southwest-desert',
    name: 'Southwest Desert',
    description: 'Terracotta, sage & adobe',
    swatch: ['#c1603a', '#7a9e7e', '#fdf6ee'],
  },
  {
    id: 'pride',
    name: 'Pride',
    description: 'Bold rainbow spectrum',
    swatch: ['#e53935', '#8e24aa', '#039be5'],
  },
  {
    id: 'transformative-teal',
    name: 'Transformative Teal',
    description: 'Blue-green & warm terracotta',
    swatch: ['#0d7d7d', '#c9743d', '#f6faf9'],
  },
  {
    id: 'warm-linen',
    name: 'Warm Linen',
    description: 'Soft browns & muted sage',
    swatch: ['#8a6d4f', '#5f7a6b', '#faf6f0'],
  },
  {
    id: 'violet-cyan',
    name: 'Violet & Cyan',
    description: 'Vivid violet & bright cyan',
    swatch: ['#7c5cff', '#12a4c4', '#faf9ff'],
  },
  {
    id: 'sage-clay',
    name: 'Sage & Clay',
    description: 'Muted green & warm clay',
    swatch: ['#6b8266', '#b5714e', '#f7f8f4'],
  },
  {
    id: 'slate-amber',
    name: 'Slate & Amber',
    description: 'Cool slate-blue & warm amber',
    swatch: ['#3d5573', '#d98b2b', '#f7f9fb'],
  },
];

export default function ThemePicker({ currentTheme, onSelectTheme, onClose }) {
  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-bold text-slate-800 mb-1">Choose a Theme</h2>
        <p className="text-xs text-slate-500 mb-5">Your choice is saved automatically.</p>

        <div className="space-y-2">
          {themes.map((theme) => {
            const isActive = currentTheme === theme.id;
            return (
              <button
                key={theme.id}
                onClick={() => { onSelectTheme(theme.id); onClose(); }}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border-2 transition-all text-left ${
                  isActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {/* Color swatches */}
                <div className="flex gap-1 flex-shrink-0">
                  {theme.swatch.map((color, i) => (
                    <div
                      key={i}
                      className="w-5 h-5 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>

                {/* Labels */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{theme.name}</p>
                  <p className="text-xs text-slate-500">{theme.description}</p>
                </div>

                {/* Active indicator */}
                {isActive && (
                  <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
